import {ReactNode} from "react";

/*
 * Competition notification family — the nine states of leaderboard-2026 kit
 * §04 plus v2's join confirmation. Copy templates use v2's wording (v2 wins
 * over the kit where they disagree — "prize line", never "the cut") and the
 * monochrome icon set (v2 §8: emoji render differently per OS and can't take
 * the skin or severity colour).
 *
 * Format layer only: firing sites (socket wiring) decide WHEN; the cadence
 * caps here are the family's own suppression rules from the kit cards.
 */

export type CompetitionToastSeverity = "up" | "down" | "warn" | "urgent" | "neutral";

export type CompetitionToastState =
    "rankUp"
    | "passed"
    | "enteredMoney"
    | "leftMoney"
    | "offPrizeLine"
    | "closingHour"
    | "finalMinutes"
    | "prizePaid"
    | "personalBest"
    | "joined";

/**
 * One payload shape across the family; each template reads the fields its
 * copy names (who, what rank, what it is worth — never a bare "rank changed").
 */
export interface CompetitionToastPayload {
    /** Rival named in the copy (passed/rankUp) */
    rivalName?: string;
    /** The rank the copy is about, formatted without "#" (added by templates) */
    rank?: number;
    /** Points needed / achieved, already formatted (e.g. "25 points", "441 points") */
    pointsLabel?: string;
    /** Prize value label (e.g. "$1 Fuel") */
    prizeLabel?: string;
    /** Time remaining label (e.g. "19h 38m", "4m 11s") */
    timeLeftLabel?: string;
    /** What the points buy back, in one action (e.g. "That's one victory.") */
    actionLabel?: string;
    /** Board name for cross-board copy (prizePaid) */
    boardLabel?: string;
    /** Rank the player can still reach (closingHour/finalMinutes) */
    targetRank?: number;
    /**
     * Cadence scope for once-per-period states (offPrizeLine, closingHour,
     * finalMinutes): pass `${leaderboardId}:${periodNumber}` so the cap
     * resets when the period rolls.
     */
    periodKey?: string;
}

export interface CompetitionToastStateConfig {
    severity: CompetitionToastSeverity;
    /** Monochrome glyph per v2 §8 — tinted by severity through the tile */
    icon: string;
    title: string;
    message: (p: CompetitionToastPayload) => ReactNode;
    /**
     * Suppression window in ms, or "oncePerPeriod" (keyed by payload.periodKey),
     * or null for "always fires" states.
     */
    cadence: number | "oncePerPeriod" | null;
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th". */
function ordinal(n: number): string {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
}

const MINUTE = 60_000;

export const competitionToastFamily: Record<CompetitionToastState, CompetitionToastStateConfig> = {
    // Rank improves and you are outside the money — batched, max one per 10 min
    rankUp: {
        severity: "up",
        icon: "▲",
        title: "You gained a place",
        message: (p) => <>You passed <b>{p.rivalName}</b>. You're now <b>#{p.rank}</b>.</>,
        cadence: 10 * MINUTE,
    },
    /*
     * A named rival overtakes you — when it changes paid status the firing
     * site bypasses the cap; otherwise once per hour
     */
    passed: {
        severity: "down",
        icon: "▼",
        title: "You were passed",
        message: (p) => <><b>{p.rivalName}</b> moved ahead of you into <b>#{p.rank}</b>.</>,
        cadence: 60 * MINUTE,
    },
    // You cross into a paid rank — always, this is the moment
    enteredMoney: {
        severity: "up",
        icon: "$",
        title: "You're in the money",
        message: (p) => <><b>#{p.rank}</b> pays <b>{p.prizeLabel}</b>. Hold it for {p.timeLeftLabel}.</>,
        cadence: null,
    },
    // You fall out of a paid rank — always
    leftMoney: {
        severity: "down",
        icon: "$",
        title: "You left the money",
        message: (p) => <><b>#{p.rank}</b> pays nothing. <b>+{p.pointsLabel}</b> puts you back in.</>,
        cadence: null,
    },
    // You sit exactly one rank below the prize line — once per period
    offPrizeLine: {
        severity: "warn",
        icon: "!",
        title: "One place off the prize line",
        message: (p) => <><b>{p.pointsLabel}</b> puts you back in. {p.actionLabel}</>,
        cadence: "oncePerPeriod",
    },
    /*
     * T-minus 60 minutes — once, and only if the player has played this
     * period (the firing site checks the played condition)
     */
    closingHour: {
        severity: "warn",
        icon: "◷",
        title: "One hour left today",
        message: (p) => <>You're <b>#{p.rank}</b>. Last chance to reach <b>#{p.targetRank}</b>.</>,
        cadence: "oncePerPeriod",
    },
    /*
     * T-minus 5 minutes within one action of the prize line — once, the only
     * red interrupt
     */
    finalMinutes: {
        severity: "urgent",
        icon: "◷",
        title: "Final minutes",
        message: (p) => <><b>{p.timeLeftLabel}</b> left. One victory reaches <b>#{p.targetRank}</b>.</>,
        cadence: "oncePerPeriod",
    },
    // Distribution completes — always
    prizePaid: {
        severity: "up",
        icon: "✓",
        /* Names the placement, not the transfer — this fires only for ranks that were paid. */
        title: "You won",
        message: (p) => p.rank === 1
            ? <>You won <b>{p.boardLabel}</b>{p.prizeLabel ? <> — <b>{p.prizeLabel}</b></> : null}.</>
            : <>You placed <b>{p.rank ? ordinal(p.rank) : "in the money"}</b> in <b>{p.boardLabel}</b>{p.prizeLabel ? <> — <b>{p.prizeLabel}</b></> : null}.</>,
        cadence: null,
    },
    // You beat your own record — always, and it is never urgent
    personalBest: {
        severity: "neutral",
        icon: "★",
        title: "New personal best",
        message: (p) => <><b>{p.pointsLabel}</b> — your best on this board.</>,
        cadence: null,
    },
    // v2 §8: join confirmation fired by the join flow
    joined: {
        severity: "up",
        icon: "✓",
        title: "You're on the board",
        message: (p) => <>Scoring starts with your next match. Today's board closes in {p.timeLeftLabel}.</>,
        cadence: null,
    },
};
